import classNames from "classnames";
import React from "react";
import { msgid, ngettext, t } from "ttag";

import { IWebPageURL } from "../../../models/nominals";
import { trackSleepExpertEvent } from "../../../utils/analytics";
import { formatPhoneNumber } from "../../../utils/format";
import urls from "../../../utils/urls";
import { ChatLink } from "../../chat/ChatLink";
import { useAppSelector } from "../../reducers";
import { nearbyStoreSelector, preferredLocationSelector } from "../selectors";

import styles from "./FooterWidgets.module.scss";

const ContactSupportWidget = (props: {
    phone: string;
    chatOfflineLink?: IWebPageURL;
}): React.ReactNode => {
    return (
        <div className={styles.contactSupport}>
            <h4 className={styles.heading}>
                <ChatLink
                    chatOnlineText={t`Talk to a Sleep Expert`}
                    chatOfflineText={t`Talk to a Sleep Expert`}
                    chatOfflineLink={
                        urls.pageURL("contact_link") || urls.pageURL("home")
                    }
                />
            </h4>
            <span className={styles.copy}>
                <ChatLink
                    chatOfflineLink={
                        props.chatOfflineLink ||
                        urls.pageURL("contact_link") ||
                        urls.pageURL("home")
                    }
                />
                {/* TODO(i18n): Use jt to keep complete sentence together: jt`${chatLink} or call ${phoneLink}` */}
                <span> or call </span>
                <a
                    className={styles.link}
                    href={`tel:${props.phone}`}
                    onClick={() => trackSleepExpertEvent("call")}
                >
                    {formatPhoneNumber(props.phone)}
                </a>
            </span>
        </div>
    );
};

const NearbyStoresWidget = (): React.ReactNode => {
    const numStores = useAppSelector(
        (state) => nearbyStoreSelector(state).length,
    );
    const location = useAppSelector((state) =>
        preferredLocationSelector(state),
    );
    const text = location
        ? ngettext(
              msgid`${numStores} store near ${location.formatted_address}`,
              `${numStores} stores near ${location.formatted_address}`,
              numStores,
          )
        : ngettext(
              msgid`${numStores} store near you`,
              `${numStores} stores near you`,
              numStores,
          );
    return (
        <div className={styles.nearbyStores}>
            <h4 className={styles.heading}>
                <a
                    title={t`Find a Retailer`}
                    href="/find-a-retailer"
                    className="footer-widget__header"
                >
                    {t`Feel it for Yourself`}
                </a>
            </h4>
            <span className={styles.copy}>
                {/* TODO(i18n): Use jt to keep complete sentence together: jt`${props.text} ${seeListLink}` */}
                <span>{text}</span>{" "}
                <a
                    className={styles.link}
                    title={t`Find a Retailer`}
                    href="/find-a-retailer"
                >
                    {t`See List`}
                </a>
            </span>
        </div>
    );
};

export const FooterWidgets = (props: {
    phoneNumber: string;
    showContactSupportWidget: boolean;
    showNearbyStoresWidget: boolean;
    chatOfflineLink?: IWebPageURL;
}): React.ReactNode => {
    const firstWidgetClasses = classNames({
        [styles.firstWidget]:
            props.showContactSupportWidget && props.showNearbyStoresWidget,
        [styles.widgetWrapper]: props.showContactSupportWidget,
    });
    if (!props.showContactSupportWidget && !props.showNearbyStoresWidget) {
        return null;
    }
    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                {props.showContactSupportWidget && (
                    <div className={firstWidgetClasses}>
                        <ContactSupportWidget
                            phone={props.phoneNumber}
                            chatOfflineLink={props.chatOfflineLink}
                        />
                    </div>
                )}
                {props.showNearbyStoresWidget && (
                    <div className={styles.widgetWrapper}>
                        <NearbyStoresWidget />
                    </div>
                )}
            </div>
        </div>
    );
};
